import React from 'react';

type ErrorBoundaryFallbackProps = {
  error: unknown;
  reset: () => void;
};

type ErrorBoundaryProps = {
  children: React.ReactNode;
  fallback: (props: ErrorBoundaryFallbackProps) => React.ReactNode;
  onError?: (error: unknown, errorInfo: React.ErrorInfo) => void;
  /**
   * When this value changes, the boundary resets itself.
   * Useful for "per tab" boundaries (e.g. Settings tab value).
   */
  resetKey?: string | number | null;
};

type ErrorBoundaryState = {
  error: unknown | null;
};

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo) {
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      // Reset when the user navigates to another tab/page.
      this.setState({ error: null });
    }
  }

  private reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      return this.props.fallback({ error: this.state.error, reset: this.reset });
    }
    return this.props.children;
  }
}

